import withLoading from '@oncolens/oncolens-ui/lib/components/Common/withLoading';
import { SectionTitle } from '@/components/Layout/SectionTitle';
import {
  TreatmentHistoryDto,
  useGetTreatmentHistories
} from '@/features/requests/api/getTreatmentHistories';

interface TreatmentHistoryProps {
	patientId: string | undefined;
}

interface TreatmentHistoryBodyProps {
	data: TreatmentHistoryDto[];
}

export const TreatmentHistory = ({ patientId }: TreatmentHistoryProps) => {
  const treatmentHistories = useGetTreatmentHistories(patientId);

  return (
    <div className="print:hidden">
      <SectionTitle title="Treatment History" />
      <TreatmentHistoryBodyWithLoading
        isLoading={treatmentHistories.isLoading}
        data={treatmentHistories.data ?? ([] as TreatmentHistoryDto[])}
      />
    </div>
  );
};

const TreatmentHistoryBody = ({ data }: TreatmentHistoryBodyProps) => {
  return data.length > 0 ? (
    <table data-cy="treatment-history-table" className="w-full table-auto">
      <thead data-cy="treatment-history-headers">
        <tr>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
						Treatment Type
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
            Treatment
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
						Dates
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
						Status
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
						Other
          </th>
        </tr>
      </thead>
      <tbody data-cy="treatment-history-values" className="text-xs">
        {data.map((treatmentHistory) => {
          return (
            <tr data-cy="treatment-history" key={treatmentHistory.id}>
              <td data-cy="treatment-history-type" className="w-1/5 p-2">{treatmentHistory.treatmentType}</td>
              <td data-cy="treatment-history-description" className="w-1/5 p-2">{treatmentHistory.treatmentDescription}</td>
              <td data-cy="treatment-history-date" className="w-1/5 p-2">
                {treatmentHistory.startDate?.toLocaleDateString()}-
                {treatmentHistory.endDate?.toLocaleDateString()}
              </td>
              <td data-cy="treatment-history-status" className="p-2">{treatmentHistory.status}</td>
              <td data-cy="treatment-history-comments" className="p-2">{treatmentHistory.comments}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  ) : (
    <div className="w-full py-8 bg-gray-100 rounded-lg">
      <p data-cy="no-treatment-history" className="w-full text-center">No treatment history has been provided yet.</p>
    </div>
  );
};

const TreatmentHistorySkeleton = () => (
  <table className="w-full table-auto">
    <thead>
      <tr>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
					Treatment
        </th>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
					Date
        </th>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
					Tissue Availability
        </th>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
					Collection Site
        </th>
      </tr>
    </thead>
    <tbody className="text-xs">
      <tr>
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
      </tr>
    </tbody>
  </table>
);

const TreatmentHistoryBodyWithLoading = withLoading(TreatmentHistoryBody, TreatmentHistorySkeleton);
